// On-demand loader for the Google Maps JS dynamic loader + Places library
// (the "New" Places API — uses google.maps.places.PlaceAutocompleteElement,
// not the deprecated google.maps.places.Autocomplete class).
//
// VITE_GOOGLE_MAPS_API_KEY must be set at build time. When unset, the
// loader resolves to null and callers should gracefully degrade — the
// address field stays a plain text input with no autocomplete.

// Minimal type declarations for the bits we use. The official
// @types/google.maps package lags the new Place API; we declare the
// methods we actually call.

type AddressComponent = {
  longText: string;
  shortText: string;
  types: string[];
};

export type Place = {
  addressComponents?: AddressComponent[];
  formattedAddress?: string;
  fetchFields(opts: { fields: string[] }): Promise<void>;
};

type PlacePrediction = {
  toPlace(): Place;
};

export type PlaceAutocompleteSelectEvent = Event & {
  placePrediction: PlacePrediction;
};

export type PlaceAutocompleteElement = HTMLElement & {
  // Options set via property assignment after construction.
  includedRegionCodes?: string[];
  // Older alias used by some doc revisions.
  componentRestrictions?: { country: string | string[] };
};

type PlaceAutocompleteElementCtor = new (opts?: {
  includedRegionCodes?: string[];
}) => PlaceAutocompleteElement;

type PlacesLibrary = {
  PlaceAutocompleteElement: PlaceAutocompleteElementCtor;
};

declare global {
  interface Window {
    google?: {
      maps?: {
        importLibrary?: (name: "places") => Promise<PlacesLibrary>;
      };
    };
  }
}

export type AddressComponents = {
  street1: string;
  city: string;
  state: string;
  zip: string;
};

let scriptPromise: Promise<PlacesLibrary | null> | null = null;

function readApiKey(): string {
  return (
    (import.meta as { env?: Record<string, string | undefined> }).env
      ?.VITE_GOOGLE_MAPS_API_KEY ?? ""
  );
}

export function isGooglePlacesEnabled(): boolean {
  return readApiKey().length > 0;
}

export function loadGooglePlaces(): Promise<PlacesLibrary | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (scriptPromise) return scriptPromise;

  const apiKey = readApiKey();
  if (!apiKey) {
    scriptPromise = Promise.resolve(null);
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    // Already loaded?
    if (window.google?.maps?.importLibrary) {
      window.google.maps
        .importLibrary("places")
        .then(resolve)
        .catch(reject);
      return;
    }
    // Inject Google's modern dynamic loader. With `loading=async` the
    // script tag's onload fires BEFORE google.maps.importLibrary is
    // actually attached — we must use the `callback=` query parameter
    // as the readiness signal. Google's loader will invoke
    // window[callback]() once importLibrary is ready.
    const CALLBACK_NAME = "__gmpsLoaderReady";
    const w = window as unknown as Record<string, unknown>;
    w[CALLBACK_NAME] = () => {
      delete w[CALLBACK_NAME];
      const importLibrary = window.google?.maps?.importLibrary;
      if (!importLibrary) {
        reject(new Error("google.maps.importLibrary missing after callback"));
        return;
      }
      importLibrary("places").then(resolve).catch(reject);
    };
    const script = document.createElement("script");
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&v=weekly&loading=async&callback=${CALLBACK_NAME}`;
    script.async = true;
    script.onerror = () => {
      delete w[CALLBACK_NAME];
      reject(new Error("Failed to load Google Maps JS"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

// Parses the New Places API's addressComponents (camelCase, longText/shortText)
// into the four fields our intake form needs. Missing components fall back
// to "".
export function parseAddressComponents(place: Pick<Place, "addressComponents">): AddressComponents {
  const comps = place.addressComponents ?? [];
  const get = (type: string, short = false): string => {
    const c = comps.find((c) => c.types.includes(type));
    if (!c) return "";
    return short ? c.shortText : c.longText;
  };
  const streetNumber = get("street_number");
  const route = get("route");
  const street1 = [streetNumber, route].filter(Boolean).join(" ");
  const city = get("locality") || get("sublocality") || get("postal_town");
  const state = get("administrative_area_level_1", true);
  const zip = get("postal_code");
  return { street1, city, state, zip };
}
